// EM2c Building System — merges legacy + EM2c building data, manages production chains
// This module is the single source of truth for building gameplay logic.
// The store calls into these functions; it does NOT duplicate building logic.

import { BUILDINGS } from "./data/buildings";
import { EM2C_BUILDINGS, EM2C_CATEGORIES } from "./data/em2c-buildings";
import { FACTORY_RECIPES, CONSTRUCTION_YARD_RECIPES, NEW_RESOURCES } from "./data/productionChains";

// ---- Merge legacy BUILDINGS with EM2c data ----
const MERGED = {};

// First, copy all legacy buildings
for (const [id, def] of Object.entries(BUILDINGS)) {
  MERGED[id] = { ...def, tier: 0, locked: false, connectionTypes: [], operationalStates: ["operational"] };
}

// Then merge EM2c buildings
for (const [id, em2c] of Object.entries(EM2C_BUILDINGS)) {
  if (MERGED[id]) {
    // Existing building — enrich with EM2c data without overwriting legacy rendering fields
    MERGED[id] = {
      ...MERGED[id],
      tier: em2c.tier ?? 0,
      locked: em2c.locked ?? false,
      connectionTypes: em2c.connectionTypes || [],
      operationalStates: ["constructing", "operational", "disabled", "no_power", "no_workers", "no_input", "output_full", "damaged"],
      workersRequired: em2c.workersRequired || 0,
      workerCapacity: em2c.workerCapacity || 0,
      powerConsumption: em2c.powerConsumption || 0,
      powerGeneration: em2c.powerGeneration || 0,
      waterConsumption: em2c.waterConsumption || 0,
      waterGeneration: em2c.waterGeneration || 0,
      oxygenConsumption: em2c.oxygenConsumption || 0,
      oxygenGeneration: em2c.oxygenGeneration || 0,
      heatGeneration: em2c.heatProduction || 0,
      productionInputs: em2c.productionInputs || {},
      productionOutputs: em2c.productionOutputs || {},
      storageCapacity: em2c.storageCapacity || {},
      maintenanceCost: em2c.maintenanceCost || {},
      researchOutput: em2c.productionOutputs?.research || 0,
      serviceCapacity: em2c.serviceCapacity || MERGED[id].houses || 0,
      shape: em2c.shape || "boxy",
    };
  } else {
    // New EM2c building — derive legacy compat fields
    const cost = em2c.cost || {};
    const powerNet = (em2c.powerGeneration || 0) - (em2c.powerConsumption || 0);
    const waterNet = (em2c.waterGeneration || 0) - (em2c.waterConsumption || 0);
    const oxygenNet = (em2c.oxygenGeneration || 0) - (em2c.oxygenConsumption || 0);

    MERGED[id] = {
      ...em2c,
      id: em2c.id,
      name: em2c.name,
      category: em2c.category,
      description: em2c.description,
      cost,
      buildTime: em2c.buildTime || 10,
      size: em2c.size || [3, 2, 3],
      color: em2c.color || "#3a3f4b",
      accent: em2c.accent || "#00e5ff",
      power: powerNet,
      water: waterNet,
      oxygen: oxygenNet,
      food: em2c.productionOutputs?.food || 0,
      metal: em2c.productionOutputs?.metal || em2c.productionOutputs?.raw_ore || 0,
      houses: em2c.serviceCapacity || 0,
      storage: em2c.storageCapacity || null,
      requiresDeposit: em2c.requiresDeposit || null,
      healRate: em2c.healRate || 0,
      workersNeeded: em2c.workersRequired || 0,
      tier: em2c.tier ?? 0,
      locked: em2c.locked ?? false,
      connectionTypes: em2c.connectionTypes || [],
      operationalStates: ["constructing", "operational", "disabled", "no_power", "no_workers", "no_input", "output_full", "damaged"],
      productionInputs: em2c.productionInputs || {},
      productionOutputs: em2c.productionOutputs || {},
      storageCapacity: em2c.storageCapacity || {},
      maintenanceCost: em2c.maintenanceCost || {},
      researchOutput: em2c.productionOutputs?.research || 0,
      shape: em2c.shape || "boxy",
    };
  }
}

export const ALL_BUILDINGS = MERGED;
export const BUILDING_LIST = Object.values(MERGED);
export { EM2C_CATEGORIES };

// ---- Resource system: merge base + new resources ----
export const EXTENDED_RESOURCES = {
  power: { id: "power", label: "Power", unit: "kW" },
  water: { id: "water", label: "Water", unit: "L" },
  oxygen: { id: "oxygen", label: "Oxygen", unit: "kg" },
  food: { id: "food", label: "Food", unit: "kg" },
  metal: { id: "metal", label: "Metal", unit: "t" },
  parts: { id: "parts", label: "Machine Parts", unit: "u" },
  polymers: { id: "polymers", label: "Polymers", unit: "u" },
  credits: { id: "credits", label: "Credits", unit: "M$" },
  ...NEW_RESOURCES,
};

// Initial state for new resources (all start at 0)
export const NEW_RESOURCE_INITIAL = {};
for (const [id] of Object.entries(NEW_RESOURCES)) {
  NEW_RESOURCE_INITIAL[id] = { stored: 0, max: 500 };
}

// ---- Building state computation ----
export function getBuildingState(building, resources, colonistCount) {
  if (building.progress < 1) return "constructing";
  if (!building.active) return "disabled";
  
  const def = ALL_BUILDINGS[building.type];
  if (!def) return "operational";

  if (def.powerConsumption > 0 && resources.power?.stored <= 0) return "no_power";
  if (def.waterConsumption > 0 && resources.water?.stored <= 0) return "no_water";
  if (def.oxygenConsumption > 0 && resources.oxygen?.stored <= 0) return "no_oxygen";
  if (def.workersRequired > 0 && colonistCount < def.workersRequired) return "no_workers";
  
  if (def.productionInputs && Object.keys(def.productionInputs).length > 0) {
    for (const [emKey, rate] of Object.entries(def.productionInputs)) {
      const gameKey = EM2C_RES_MAP[emKey] || emKey;
      if (rate > 0 && (resources[gameKey]?.stored || 0) <= 0) return "no_input";
    }
  }
  
  if (def.productionOutputs && Object.keys(def.productionOutputs).length > 0) {
    for (const [emKey, rate] of Object.entries(def.productionOutputs)) {
      const gameKey = EM2C_RES_MAP[emKey] || emKey;
      if (rate > 0 && resources[gameKey]?.stored >= (resources[gameKey]?.max || 500)) return "output_full";
    }
  }
  
  if (building.condition < 0.4) return "damaged";
  return "operational";
}

// ---- Production chain tick ----
// EM2c resource names → game resource names
const EM2C_RES_MAP = {
  power: "power", raw_water: "water", clean_water: "water", water: "water",
  oxygen: "oxygen", food: "food", raw_food: "food", processed_food: "food",
  metal: "metal", metal_ore: "metal", refined_metal: "metal", sorted_ore: "metal",
  parts: "parts", machine_parts: "parts", polymers: "polymers", credits: "credits",
};

export function computeProductionNet(buildings, resources, dt, modifiers = {}) {
  const net = {};
  
  for (const b of buildings) {
    if (b.progress < 1 || !b.active || b.controllerShed) continue;
    
    const def = ALL_BUILDINGS[b.type];
    if (!def) continue;
    
    const hasInputs = def.productionInputs && Object.keys(def.productionInputs).length > 0;
    const hasOutputs = def.productionOutputs && Object.keys(def.productionOutputs).length > 0;
    
    if (!hasInputs && !hasOutputs) continue;
    
    let canProduce = true;
    
    if (def.powerConsumption > 0 && resources.power?.stored <= 0) canProduce = false;
    
    if (hasInputs && canProduce) {
      for (const [emKey, rate] of Object.entries(def.productionInputs)) {
        const gameKey = EM2C_RES_MAP[emKey] || emKey;
        if (rate > 0 && (resources[gameKey]?.stored || 0) < rate * dt) {
          canProduce = false;
          break;
        }
      }
    }
    
    if (hasOutputs && canProduce) {
      for (const [emKey, rate] of Object.entries(def.productionOutputs)) {
        if (rate > 0) {
          const gameKey = EM2C_RES_MAP[emKey] || emKey;
          const max = resources[gameKey]?.max || 500;
          if ((resources[gameKey]?.stored || 0) >= max) {
            canProduce = false;
            break;
          }
        }
      }
    }
    
    if (!canProduce) continue;
    
    const efficiency = (b.condition || 1) * (modifiers.efficiency || 1);
    
    if (hasInputs) {
      for (const [emKey, rate] of Object.entries(def.productionInputs)) {
        const gameKey = EM2C_RES_MAP[emKey] || emKey;
        net[gameKey] = (net[gameKey] || 0) - rate * efficiency * dt;
      }
    }
    if (hasOutputs) {
      for (const [emKey, rate] of Object.entries(def.productionOutputs)) {
        const gameKey = EM2C_RES_MAP[emKey] || emKey;
        net[gameKey] = (net[gameKey] || 0) + rate * efficiency * dt;
      }
    }
  }
  
  return net;
}

// ---- Factory recipe system ----
export function getRecipes(buildingId) {
  return FACTORY_RECIPES[buildingId] || CONSTRUCTION_YARD_RECIPES[buildingId] || [];
}

export const RECIPE_BUILDINGS = new Set([
  "factory_complex", "component_manufacturing", "component_factory",
  "additive_manufacturing", "print_factory", "construction_yard",
]);

export function isRecipeBuilding(buildingId) {
  return RECIPE_BUILDINGS.has(buildingId);
}

export function processRecipeTick(building, selectedRecipe, resources, dt) {
  if (!selectedRecipe || building.progress < 1 || !building.active) return null;
  
  const recipe = getRecipes(building.type).find(r => r.id === selectedRecipe);
  if (!recipe) return null;
  
  // Check inputs available
  for (const [resource, amount] of Object.entries(recipe.inputs)) {
    if ((resources[resource]?.stored || 0) < amount) return null;
  }
  
  // Check output storage
  for (const [resource, amount] of Object.entries(recipe.outputs)) {
    const max = resources[resource]?.max || 500;
    if ((resources[resource]?.stored || 0) + amount > max) return null;
  }
  
  const progress = (building.recipeProgress || 0) + dt;
  if (progress < recipe.time) {
    return { recipeProgress: progress };
  }
  
  // Recipe complete
  const net = {};
  for (const [resource, amount] of Object.entries(recipe.inputs)) {
    net[resource] = (net[resource] || 0) - amount;
  }
  for (const [resource, amount] of Object.entries(recipe.outputs)) {
    net[resource] = (net[resource] || 0) + amount;
  }
  
  return { net, recipeProgress: 0 };
}

// ---- Tier unlock system ----
export function getUnlockedBuildings(colonyTier) {
  return Object.entries(ALL_BUILDINGS)
    .filter(([, def]) => (def.tier ?? 0) <= colonyTier && !def.locked)
    .map(([id]) => id);
}

export function getBuildingsByTier(tier) {
  return Object.entries(ALL_BUILDINGS)
    .filter(([, def]) => (def.tier ?? 0) === tier)
    .map(([, def]) => def);
}

// ---- Category mapping (EM2c categories to UI tabs) ----
export function getCategoryForBuilding(buildingId) {
  const def = ALL_BUILDINGS[buildingId];
  if (!def) return "infra";
  const categoryMap = {
    core: "infra", habitation: "habitat", medical: "life", life: "life",
    power: "power", mining: "production", manufacturing: "production",
    storage: "storage", logistics: "storage", drones: "infra",
    spaceport: "infra", research: "production", communications: "infra",
    expansion: "infra",
  };
  return categoryMap[def.category] || def.category || "infra";
}

// ---- Get building definition (merged) ----
export function getBuildingDef(id) {
  return ALL_BUILDINGS[id] || BUILDINGS[id];
}

export default {
  ALL_BUILDINGS,
  BUILDING_LIST,
  EM2C_CATEGORIES,
  EXTENDED_RESOURCES,
  NEW_RESOURCE_INITIAL,
  getBuildingState,
  computeProductionNet,
  getRecipes,
  isRecipeBuilding,
  processRecipeTick,
  getUnlockedBuildings,
  getBuildingsByTier,
  getCategoryForBuilding,
  getBuildingDef,
};
